/**
 * MarkerManager - Handles DOM marker rendering, styling, popups, and interactions
 * Manages Mapbox GL JS DOM markers lifecycle
 */
class MarkerManager {
    static CONFIG = {
        // Zoom threshold for showing markers
        MIN_ZOOM: 11, // Only show markers at zoom >= 11
        // Marker types
        MARKER_TYPES: {
            FEATURED: 'featured', // Type A: Large (40px)
            NON_FEATURED_WITH_PHOTO: 'with-photo', // Type B: Medium (30px)
            NON_FEATURED_NO_PHOTO: 'no-photo' // Type C: Small (12px)
        },
        // Marker sizes
        SIZES: {
            FEATURED_BASE: '50px',
            FEATURED_HOVER: '75px', // 1.5x of 50px
            WITH_PHOTO_BASE: '40px',
            WITH_PHOTO_HOVER: '75px', // 1.5x of 40px
            NO_PHOTO_BASE: '16px',
            NO_PHOTO_HOVER: '24px' // 1.5x of 16px
        },
        // Z-index management
        Z_INDEX: {
            BASE: 1,
            ACTIVE: 2,
            HOVER: 3
        },
        // Marker styling
        MARKER: {
            BORDER_RADIUS: '50%',
            BORDER_WIDTH: '2px',
            BORDER_COLOR_DEFAULT: 'white',
            CURSOR: 'pointer',
            CIRCLE_SIZE: '8px',
            CIRCLE_OFFSET: '4px'
        },
        // Zoom levels for progressive zooming (matching legacy app)
        ZOOM: {
            FIRST_CLICK: 13,      // Initial zoom when clicking marker from far away
            SECOND_CLICK: 15,     // Second level zoom
            THIRD_CLICK: 18.5,    // Final zoom level with 3D view
            THRESHOLD_LOW: 12.5,    // Below this, zoom to FIRST_CLICK
            THRESHOLD_MID: 14.5,    // Below this, zoom to SECOND_CLICK
            THRESHOLD_HIGH: 18    // Below this, zoom to THIRD_CLICK
        },
        // Fly animation settings
        FLY: {
            SPEED: 0.1,
            CURVE: 2,
            DURATION_FAST: 7500,
            DURATION_SLOW: 10000,
            PITCH_3D: 20,
            PITCH_FLAT: 0,
            BEARING: 0
        }
    };
    /**
     * Create MarkerManager instance
     * @param {Object} mapManager - MapManager instance
     * @param {Object} dataManager - DataManager instance
     */
    constructor(mapManager, dataManager) {
        if (!mapManager || !dataManager) {
            throw new Error('MarkerManager requires both mapManager and dataManager');
        }
        this.mapManager = mapManager;
        this.dataManager = dataManager;
        // Storage for all marker instances
        this.markers = [];
        // State
        this.markersRendered = false;
        this.zoomHandler = null;
        this.activeMarker = null;
        this.activeMarkerSlug = null;
        // Set up zoom-based visibility
        this._setupZoomHandler();
        // Set up map click handler for deactivation
        this._setupMapClickHandler();
    }
    /**
     * Set up zoom handler to show/hide markers
     * @private
     */
    _setupZoomHandler() {
        const map = this.mapManager.getMap();
        if (!map) return;
        // Listen for zoom changes
        this.zoomHandler = () => {
            const currentZoom = map.getZoom();
            this.toggleMarkersVisibility(currentZoom);
        };
        map.on('zoom', this.zoomHandler);
    }
    /**
     * Set up map click handler to deactivate markers
     * @private
     */
    _setupMapClickHandler() {
        const map = this.mapManager.getMap();
        if (!map) return;
        map.on('click', (e) => {
            // Only deactivate if clicking on the map, not on a marker
            const clickedOnMarker = e.originalEvent.target.closest('.marker');
            if (!clickedOnMarker) {
                // Deactivate project through UIManager (which also handles markers)
                if (window.uiManager && window.uiManager.activeProjectSlug) {
                    // Recenter map before deactivating (UIManager handles coordinate lookup)
                    window.uiManager.recenterMapToActiveMarker({ withPanelPadding: false });
                    window.uiManager.deactivateProject(window.uiManager.activeProjectSlug);
                }
                // Close both panels if they're open
                if (window.uiManager) {
                    window.uiManager.closeProjectsPanel();
                    window.uiManager.closeFiltersPanel();
                }
            }
        });
    }
    /**
     * Render all markers on the map
     * @returns {number} Number of markers rendered
     */
    renderMarkers() {
        const map = this.mapManager.getMap();
        if (!map) {
            return 0;
        }
        // Clear existing markers first
        this.clearMarkers();
        const locations = this.dataManager.allLocations;
        locations.forEach((feature) => {
            const marker = this._createMarker(feature);
            if (marker) {
                marker.addTo(map);
                this.markers.push(marker);
            }
        });
        this.markersRendered = true;
        // Set initial visibility based on current zoom
        const currentZoom = map.getZoom();
        this.toggleMarkersVisibility(currentZoom);
        return this.markers.length;
    }
    // ! CREATE MARKER FROM CREATE MARKER
    /**
     * Create a single marker from GeoJSON feature
     * @param {Object} feature - GeoJSON feature with properties
     * @returns {mapboxgl.Marker|null} Mapbox marker instance
     * @private
     */
    _createMarker(feature) {
        const { coordinates } = feature.geometry;
        const {
            id,
            typologyId,
            typologyName,
            projectName,
            photoThumbnailUrl,
            isFeatured,
            statusName
        } = feature.properties;
        // Determine marker type
        const markerType = this._getMarkerType(isFeatured, photoThumbnailUrl);
        // Create marker element
        const markerEl = this._createMarkerElement(typologyId, photoThumbnailUrl, markerType);
        // Store marker metadata
        markerEl.dataset.slug = id;
        markerEl.dataset.markerType = markerType;
        markerEl.dataset.projectName = projectName;
        markerEl.dataset.typologyName = typologyName || '';
        markerEl.dataset.statusName = statusName || 'Unknown';
        // Create and return Mapbox marker with bottom anchor
        const marker = new mapboxgl.Marker({
            element: markerEl,
            anchor: 'bottom'
        })
            .setLngLat(coordinates);
        // Add click handler
        this._setupMarkerClickHandler(markerEl, id, coordinates);
        // Add hover handlers
        this._setupMarkerHoverHandlers(markerEl, markerType);
        return marker;
    }
    /**
     * Determine marker type based on feature properties
     * @param {boolean} isFeatured - Whether project is featured
     * @param {string|null} photoThumbnailUrl - Photo URL
     * @returns {string} Marker type
     * @private
     */
    _getMarkerType(isFeatured, photoThumbnailUrl) {
        if (isFeatured) {
            return MarkerManager.CONFIG.MARKER_TYPES.FEATURED;
        } else {
            // PERFORMANCE: Non-featured markers don't load photos anymore
            // Only featured markers show photo thumbnails
            return MarkerManager.CONFIG.MARKER_TYPES.NON_FEATURED_NO_PHOTO;
        }
    }
    // ! CREATE MARKER DOM FROM STYLING 
    /**
     * Create marker DOM element with styling
     * @param {string} typologyId - Typology ID for color lookup
     * @param {string|null} photoThumbnailUrl - Optional thumbnail URL
     * @param {string} markerType - Marker type (featured, with-photo, no-photo)
     * @returns {HTMLElement} Styled marker element
     * @private
     */
    _createMarkerElement(typologyId, photoThumbnailUrl, markerType) {
        const markerEl = document.createElement('div');
        markerEl.className = 'marker';
        // Create inner circle wrapper positioned above anchor point
        const circleWrapper = document.createElement('div');
        circleWrapper.className = 'marker-circle-wrapper';
        circleWrapper.style.position = 'absolute';
        circleWrapper.style.bottom = '10px'; // 10px = 6px (line start) + 4px (gap)
        circleWrapper.style.left = '50%';
        circleWrapper.style.transform = 'translateX(-50%)';
        circleWrapper.style.pointerEvents = 'auto';
        markerEl.appendChild(circleWrapper);
        // PERFORMANCE OPTIMIZATION: Labels created on-demand during hover
        // This reduces initial DOM size significantly (3 divs vs 0 per marker)
        const color = this.dataManager.getTypologyColor(typologyId);
        // Style marker circle wrapper based on type
        // PERFORMANCE: Only load images for featured markers
        if (markerType === MarkerManager.CONFIG.MARKER_TYPES.FEATURED && photoThumbnailUrl && photoThumbnailUrl !== 'null') {
            this._styleImageMarker(circleWrapper, color, photoThumbnailUrl, markerType);
        } else {
            this._styleSolidMarker(circleWrapper, color, markerType);
        }
        return markerEl;
    }
    // ! STYLE MARKERS
    /**
     * Apply styling to image marker
     * @param {HTMLElement} element - Marker element
     * @param {string} borderColor - Border color (typology color)
     * @param {string} imageUrl - Background image URL
     * @param {string} markerType - Marker type
     * @private
     */
    _styleImageMarker(element, borderColor, imageUrl, markerType) {
        const config = MarkerManager.CONFIG;
        const sizes = config.SIZES;
        // Determine base size
        let baseSize;
        if (markerType === config.MARKER_TYPES.FEATURED) {
            baseSize = sizes.FEATURED_BASE;
        } else {
            baseSize = sizes.WITH_PHOTO_BASE;
        }
        element.style.width = baseSize;
        element.style.height = baseSize;
        element.style.borderRadius = config.MARKER.BORDER_RADIUS;
        element.style.border = `${config.MARKER.BORDER_WIDTH} solid ${borderColor}`;
        element.style.backgroundImage = `url(${imageUrl})`;
        element.style.backgroundSize = 'cover';
        element.style.backgroundPosition = 'center';
        element.style.cursor = config.MARKER.CURSOR;
        element.style.zIndex = config.Z_INDEX.BASE;
        element.style.boxShadow = '0 6px 10px rgba(0, 0, 0, 0.8)';
    }
    /**
     * Apply styling to solid color marker
     * @param {HTMLElement} element - Marker element
     * @param {string} backgroundColor - Background color (typology color)
     * @param {string} markerType - Marker type
     * @private
     */
    _styleSolidMarker(element, backgroundColor, markerType) {
        const config = MarkerManager.CONFIG;
        element.style.backgroundColor = backgroundColor;
        element.style.width = config.SIZES.NO_PHOTO_BASE;
        element.style.height = config.SIZES.NO_PHOTO_BASE;
        element.style.borderRadius = config.MARKER.BORDER_RADIUS;
        element.style.border = `${config.MARKER.BORDER_WIDTH} solid ${config.MARKER.BORDER_COLOR_DEFAULT}`;
        element.style.cursor = config.MARKER.CURSOR;
        element.style.zIndex = config.Z_INDEX.BASE;
        element.style.boxShadow = '0 6px 10px rgba(0, 0, 0, 0.6)';
    }
    /**
     * Create marker labels on-demand (performance optimization)
     * @param {HTMLElement} markerEl - Marker element
     * @returns {HTMLElement} Labels container
     * @private
     */
    _createMarkerLabels(markerEl) {
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (!circleWrapper) return null;
        // Create labels container
        const labelsContainer = document.createElement('div');
        labelsContainer.className = 'marker-labels-container';
        Object.assign(labelsContainer.style, {
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: '8px',
            gap: '4px',
            pointerEvents: 'auto',
            opacity: '0',
            transition: 'opacity 0.2s ease',
            zIndex: '1000'
        });
        // Create project name label
        const labelEl = document.createElement('div');
        labelEl.className = 'marker-label is--caps';
        Object.assign(labelEl.style, {
            color: 'black',
            padding: '3px',
            fontFamily: 'Twkeverett Book, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            lineHeight: '0.95',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textShadow: '0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5)',
            margin: '0'
        });
        labelsContainer.appendChild(labelEl);
        // Create pills container
        const pillsContainer = document.createElement('div');
        pillsContainer.className = 'marker-pills-container';
        Object.assign(pillsContainer.style, {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px'
        });
        // pill styling
        const pillStyle = {
            color: 'black',
            padding: '2px',
            fontFamily: 'Twkeverett Book, Arial, sans-serif',
            fontSize: '10px',
            fontWeight: '400',
            lineHeight: '0.95',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            textShadow: '0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5), 0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.2em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.3em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5),0 0 0.4em rgba(255,255,255,0.5)',
            margin: '0'
        };
        // Create typology label
        const typologyLabel = document.createElement('div');
        typologyLabel.className = 'marker-label-typology is--caps';
        Object.assign(typologyLabel.style, pillStyle);
        pillsContainer.appendChild(typologyLabel);
        // Create status label
        const statusLabel = document.createElement('div');
        statusLabel.className = 'marker-label-status is--caps';
        Object.assign(statusLabel.style, pillStyle);
        pillsContainer.appendChild(statusLabel);
        labelsContainer.appendChild(pillsContainer);
        circleWrapper.appendChild(labelsContainer);
        return labelsContainer;
    }
    // ! MARKER INTERACTION HANDLERS
    /**
     * Progressive zoom to marker coordinates based on current zoom level
     * Mimics legacy app behavior with 3D pitch at closer zoom levels
     * @param {Array} coordinates - [lng, lat]
     * @private
     */
    _flyToMarker(coordinates) {
        const map = this.mapManager.getMap();
        if (!map) return;
        const config = MarkerManager.CONFIG;
        const currentZoom = map.getZoom();
        // Calculate padding for sidebar offset
        const padding = this._getMarkerPadding();
        // Progressive zoom based on current zoom level (matching legacy app)
        if (currentZoom < config.ZOOM.THRESHOLD_LOW) {
            // Far away - zoom to level 13
            map.flyTo({
                center: coordinates,
                zoom: config.ZOOM.FIRST_CLICK,
                speed: config.FLY.SPEED,
                curve: config.FLY.CURVE,
                duration: config.FLY.DURATION_FAST,
                essential: true,
                pitch: config.FLY.PITCH_FLAT,
                bearing: config.FLY.BEARING,
                padding: padding,
                easing: (t) => 1 - Math.pow(1 - t, 6)
            });
        } else if (currentZoom <= config.ZOOM.THRESHOLD_MID) {
            // Medium distance - zoom to level 17
            map.flyTo({
                center: coordinates,
                zoom: config.ZOOM.SECOND_CLICK,
                speed: config.FLY.SPEED,
                curve: config.FLY.CURVE,
                duration: config.FLY.DURATION_FAST,
                essential: true,
                pitch: config.FLY.PITCH_FLAT,
                bearing: config.FLY.BEARING,
                padding: padding,
                easing: (t) => 1 - Math.pow(1 - t, 6),
            });
        } else if (currentZoom <= config.ZOOM.THRESHOLD_HIGH) {
            // Close - zoom to level 18.5 with 3D pitch
            map.flyTo({
                center: coordinates,
                zoom: config.ZOOM.THIRD_CLICK,
                speed: config.FLY.SPEED,
                curve: config.FLY.CURVE,
                duration: config.FLY.DURATION_FAST,
                essential: true,
                pitch: config.FLY.PITCH_3D,
                bearing: config.FLY.BEARING,
                padding: padding,
                easing: (t) => 1 - Math.pow(1 - t, 6),
            });
        } else {
            // Already at max zoom - recenter with 3D view
            map.flyTo({
                center: coordinates,
                zoom: currentZoom,
                speed: config.FLY.SPEED,
                curve: config.FLY.CURVE,
                duration: config.FLY.DURATION_SLOW,
                essential: true,
                pitch: config.FLY.PITCH_3D,
                bearing: config.FLY.BEARING,
                padding: padding,
                easing: (t) => 1 - Math.pow(1 - t, 6),
            });
        }
    }
    /**
     * Get marker padding to account for sidebar
     * @returns {Object} Padding object for flyTo - guaranteed to have numeric values
     * @private
     */
    _getMarkerPadding() {
        // Default padding with all numeric values
        const defaultPadding = { top: 0, bottom: 0, left: 0, right: 0 };
        // Check if we have uiManager available
        if (!window.uiManager) {
            return defaultPadding;
        }
        // Get padding from uiManager if available
        if (typeof window.uiManager.getMapPadding === 'function') {
            const padding = window.uiManager.getMapPadding();
            // Validate and ensure all values are numbers
            return {
                top: Number(padding.top) || 0,
                bottom: Number(padding.bottom) || 0,
                left: Number(padding.left) || 0,
                right: Number(padding.right) || 0
            };
        }
        return defaultPadding;
    }
    /**
     * Setup click handler for marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} slug - Project slug
     * @param {Array} coordinates - [lng, lat]
     * @private
     */
    _setupMarkerClickHandler(markerEl, slug, coordinates) {
        markerEl.addEventListener('click', (e) => {
            e.stopPropagation();
            const map = this.mapManager.getMap();
            if (!map) return;
            // Check if this marker is already active
            const isAlreadyActive = this.activeMarkerSlug === slug;
            if (!isAlreadyActive) {
                // First click - activate and zoom progressively
                this._flyToMarker(coordinates);
                // Activate marker
                this.activateMarker(markerEl, slug);
                // Open projects panel and activate project
                if (window.uiManager) {
                    window.uiManager.openProjectsPanel();
                    window.uiManager.activateProject(slug);
                }
            } else {
                // Clicking already active marker - zoom in further with 3D view
                this._flyToMarker(coordinates);
            }
        });
    }
    /**
     * Setup hover handlers for marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} markerType - Marker type
     * @private
     */
    _setupMarkerHoverHandlers(markerEl, markerType) {
        const config = MarkerManager.CONFIG;
        markerEl.addEventListener('mouseenter', () => {
            // If marker is already active with top z-index, don't change anything
            const isActive = markerEl.classList.contains('marker-active');
            if (isActive && markerEl.style.zIndex == config.Z_INDEX.ACTIVE) {
                return;
            }
            // PERFORMANCE: Create labels on-demand
            let labelsContainer = markerEl.querySelector('.marker-labels-container');
            if (!labelsContainer) {
                labelsContainer = this._createMarkerLabels(markerEl);
            }
            // Show labels container and populate labels
            if (labelsContainer) {
                labelsContainer.style.opacity = '1';
                const labelEl = labelsContainer.querySelector('.marker-label');
                if (labelEl && markerEl.dataset.projectName) {
                    labelEl.textContent = markerEl.dataset.projectName;
                }
                const typologyLabel = labelsContainer.querySelector('.marker-label-typology');
                if (typologyLabel && markerEl.dataset.typologyName) {
                    typologyLabel.textContent = markerEl.dataset.typologyName;
                }
                const statusLabel = labelsContainer.querySelector('.marker-label-status');
                if (statusLabel && markerEl.dataset.statusName) {
                    statusLabel.textContent = markerEl.dataset.statusName;
                }
            }
            // Set hover z-index (always on top)
            markerEl.style.zIndex = config.Z_INDEX.HOVER;
            // Scale up the circle wrapper based on marker type
            const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
            if (circleWrapper) {
                let hoverSize;
                if (markerType === config.MARKER_TYPES.FEATURED) {
                    hoverSize = config.SIZES.FEATURED_HOVER;
                } else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) {
                    hoverSize = config.SIZES.WITH_PHOTO_HOVER;
                } else {
                    hoverSize = config.SIZES.NO_PHOTO_HOVER;
                }
                circleWrapper.style.width = hoverSize;
                circleWrapper.style.height = hoverSize;
            }
        });
        markerEl.addEventListener('mouseleave', () => {
            // Check if marker is active
            const isActive = markerEl.classList.contains('marker-active');
            // If marker is active (clicked), keep it at active size and z-index
            if (isActive) {
                // Keep label visible for active marker
                markerEl.style.zIndex = config.Z_INDEX.ACTIVE;
                // Keep active size on circle wrapper
                const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
                if (circleWrapper) {
                    let activeSize;
                    if (markerType === config.MARKER_TYPES.FEATURED) {
                        activeSize = config.SIZES.FEATURED_HOVER;
                    } else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) {
                        activeSize = config.SIZES.WITH_PHOTO_HOVER;
                    } else {
                        activeSize = config.SIZES.NO_PHOTO_HOVER;
                    }
                    circleWrapper.style.width = activeSize;
                    circleWrapper.style.height = activeSize;
                }
            } else {
                // Hide labels container for non-active markers
                const labelsContainer = markerEl.querySelector('.marker-labels-container');
                if (labelsContainer) {
                    labelsContainer.style.opacity = '0';
                }
                // Reset z-index and size for non-active markers
                markerEl.style.zIndex = config.Z_INDEX.BASE;
                const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
                if (circleWrapper) {
                    let baseSize;
                    if (markerType === config.MARKER_TYPES.FEATURED) {
                        baseSize = config.SIZES.FEATURED_BASE;
                    } else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) {
                        baseSize = config.SIZES.WITH_PHOTO_BASE;
                    } else {
                        baseSize = config.SIZES.NO_PHOTO_BASE;
                    }
                    circleWrapper.style.width = baseSize;
                    circleWrapper.style.height = baseSize;
                }
            }
        });
    }
    /**
     * Activate a marker
     * @param {HTMLElement} markerEl - Marker element
     * @param {string} slug - Project slug
     * @public
     */
    activateMarker(markerEl, slug) {
        // Deactivate previous marker
        if (this.activeMarker && this.activeMarker !== markerEl) {
            this.deactivateMarker(this.activeMarker);
        }
        const markerType = markerEl.dataset.markerType;
        const config = MarkerManager.CONFIG;
        // PERFORMANCE: Create labels on-demand if not exist
        let labelsContainer = markerEl.querySelector('.marker-labels-container');
        if (!labelsContainer) {
            labelsContainer = this._createMarkerLabels(markerEl);
        }
        // Show labels container and populate labels
        if (labelsContainer) {
            labelsContainer.style.opacity = '1';
            const labelEl = labelsContainer.querySelector('.marker-label');
            if (labelEl && markerEl.dataset.projectName) {
                labelEl.textContent = markerEl.dataset.projectName;
            }
            const typologyLabel = labelsContainer.querySelector('.marker-label-typology');
            if (typologyLabel && markerEl.dataset.typologyName) {
                typologyLabel.textContent = markerEl.dataset.typologyName;
            }
            const statusLabel = labelsContainer.querySelector('.marker-label-status');
            if (statusLabel && markerEl.dataset.statusName) {
                statusLabel.textContent = markerEl.dataset.statusName;
            }
        }
        // Add active class
        markerEl.classList.add('marker-active');
        // Set active z-index
        markerEl.style.zIndex = config.Z_INDEX.ACTIVE;
        // Scale circle wrapper to hover size when active
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (circleWrapper) {
            let activeSize;
            if (markerType === config.MARKER_TYPES.FEATURED) {
                activeSize = config.SIZES.FEATURED_HOVER; // 60px (1.5x)
            } else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) {
                activeSize = config.SIZES.WITH_PHOTO_HOVER; // 50px
            } else {
                activeSize = config.SIZES.NO_PHOTO_HOVER; // 18px (1.5x)
            }
            circleWrapper.style.width = activeSize;
            circleWrapper.style.height = activeSize;
        }
        // Store active marker
        this.activeMarker = markerEl;
        this.activeMarkerSlug = slug;
    }
    /**
     * Deactivate a marker
     * @param {HTMLElement} markerEl - Marker element
     * @public
     */
    deactivateMarker(markerEl) {
        if (!markerEl) return;
        const markerType = markerEl.dataset.markerType;
        const config = MarkerManager.CONFIG;
        const labelsContainer = markerEl.querySelector('.marker-labels-container');
        // Hide labels container
        if (labelsContainer) {
            labelsContainer.style.opacity = '0';
        }
        // Remove active class
        markerEl.classList.remove('marker-active');
        // Reset z-index
        markerEl.style.zIndex = config.Z_INDEX.BASE;
        // Reset circle wrapper to base size
        const circleWrapper = markerEl.querySelector('.marker-circle-wrapper');
        if (circleWrapper) {
            let baseSize;
            if (markerType === config.MARKER_TYPES.FEATURED) {
                baseSize = config.SIZES.FEATURED_BASE;
            } else if (markerType === config.MARKER_TYPES.NON_FEATURED_WITH_PHOTO) {
                baseSize = config.SIZES.WITH_PHOTO_BASE;
            } else {
                baseSize = config.SIZES.NO_PHOTO_BASE;
            }
            circleWrapper.style.width = baseSize;
            circleWrapper.style.height = baseSize;
        }
    }
    /**
     * Deactivate all markers
     * @param {boolean} skipUISync - Skip UI synchronization to prevent circular calls
     * @public
     */
    deactivateAllMarkers(skipUISync = false) {
        if (this.activeMarker) {
            this.deactivateMarker(this.activeMarker);
            const wasActiveSlug = this.activeMarkerSlug;
            this.activeMarker = null;
            this.activeMarkerSlug = null;
            // Deactivate project in UI (only if not called from UI)
            if (!skipUISync && window.uiManager && wasActiveSlug) {
                window.uiManager._deactivateProjectElement(document.getElementById(wasActiveSlug));
                window.uiManager.activeProjectSlug = null;
            }
        }
    }
    /**
     * Activate marker by project slug and fly to it
     * @param {string} slug - Project slug
     * @public
     */
    activateMarkerBySlug(slug, options = {}) {
        // Find the Mapbox marker object that has a matching slug
        const mapboxMarker = this.markers.find(marker => {
            const element = marker.getElement();
            return element && element.dataset.slug === slug;
        });
        if (mapboxMarker) {
            const markerEl = mapboxMarker.getElement();
            const lngLat = mapboxMarker.getLngLat();
            
            // If this is initial load from URL, set mid-zoom without flying
            if (options.isInitialLoad) {
                const map = this.mapManager.getMap();
                if (map) {
                    // Set zoom to a mid-level (11) to show the marker region without flying
                    map.setZoom(15);
                    map.setPitch(0);
                    map.setCenter([lngLat.lng, lngLat.lat]);
                }
            } else {
                // Normal behavior - fly to the marker location using progressive zoom
                this._flyToMarker([lngLat.lng, lngLat.lat]);
            }
            
            // Activate the marker
            this.activateMarker(markerEl, slug);
        }
    }
    // ! CLEAR
    /**
     * Clear all markers from the map
     */
    clearMarkers() {
        if (this.markers.length === 0) {
            return;
        }
        this.markers.forEach(marker => {
            marker.remove();
        });
        this.markers = [];
        this.markersRendered = false;
    }
    // ! UPDATE MARKERS (FOR FILTERING)
    /**
     * Update markers based on filtered data
     * @param {Array} filteredLocations - Filtered GeoJSON features
     */
    updateMarkers(filteredLocations) {
        const map = this.mapManager.getMap();
        if (!map) {
            return;
        }
        // Clear existing markers
        this.clearMarkers();
        filteredLocations.forEach((feature) => {
            const marker = this._createMarker(feature);
            if (marker) {
                marker.addTo(map);
                this.markers.push(marker);
            }
        });
        // Set visibility based on current zoom
        const currentZoom = map.getZoom();
        this.toggleMarkersVisibility(currentZoom);
    }
    /**
     * Toggle marker visibility based on zoom level
     * Markers only visible at zoom >= MIN_ZOOM (11)
     * @param {number} zoom - Current zoom level
     */
    toggleMarkersVisibility(zoom) {
        if (this.markers.length === 0) return;
        const shouldShow = zoom >= MarkerManager.CONFIG.MIN_ZOOM;
        const display = shouldShow ? 'block' : 'none';
        this.markers.forEach(marker => {
            const element = marker.getElement();
            if (element) {
                element.style.display = display;
            }
        });
    }
    /**
     * Get specific marker by ID
     * @param {string} locationId - Location ID
     * @returns {mapboxgl.Marker|null} Marker instance or null
     */
    getMarkerById(locationId) {
        return null;
    }
    // ! FLY-TO-MARKER
    /**
     * Fly to specific marker
     * @param {string} locationId - Location ID to fly to
     */
    flyToMarker(locationId) {
        // Find the location in data
        const location = this.dataManager.allLocations.find(
            loc => loc.properties.id === locationId
        );
        if (!location) {
            return;
        }
        const [lng, lat] = location.geometry.coordinates;
        // ! Use MapManager to fly to location
        this.mapManager.flyTo({
            center: { lng, lat },
            zoom: 12
        });
    }
    // ! GET-MARKER-COUNT
    /**
     * Get total number of rendered markers
     * @returns {number} Number of markers
     */
    getMarkerCount() {
        return this.markers.length;
    }
    // ! ARE-MARKERS-RENDERED
    /**
     * Check if markers have been rendered
     * @returns {boolean} True if markers are rendered
     */
    areMarkersRendered() {
        return this.markersRendered;
    }
    /**
     * Update marker styling configuration
     * @param {Object} config - New configuration to merge
     */
    updateConfig(config) {
        Object.assign(MarkerManager.CONFIG, config);
    }
}
// ! EXPORT
export default MarkerManager;
// Legacy support for non-module environments
if (typeof window !== 'undefined') {
    window.MarkerManager = MarkerManager;
}